import { useState, useRef } from 'react';
import { Send, CheckCircle, Loader2, Mail, ChevronUp } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentTheme, withLangAndTheme } from '@/utils/siteSync';

const RATE_LIMIT_MS = 300_000; // 5 min between submissions

export function NewsletterForm() {
  const { t, language } = useLanguage();
  const theme = useCurrentTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [gdprChecked, setGdprChecked] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const lastSubmitRef = useRef(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !gdprChecked) return;

    // Honeypot check – real users never fill this
    if (honeypot) return;

    // Client-side rate limit
    const now = Date.now();
    if (now - lastSubmitRef.current < RATE_LIMIT_MS) {
      setStatus('error');
      setMessage(t.newsletter.error);
      return;
    }
    lastSubmitRef.current = now;

    setStatus('loading');
    try {
      const { data, error } = await supabase.functions.invoke('subscribe-newsletter', {
        body: { email: email.trim(), language, _hp: honeypot },
      });
      if (error) throw error;

      if (data?.alreadySubscribed) {
        setMessage(t.newsletter.alreadySubscribed);
      } else {
        setMessage(t.newsletter.success);
      }
      setStatus('success');
      setEmail('');
      setGdprChecked(false);
    } catch {
      setStatus('error');
      setMessage(t.newsletter.error);
    }
  };

  return (
    <div className="newsletter-footer-wrapper">
      {/* Expandable form panel */}
      <div className={`newsletter-panel ${isOpen ? 'newsletter-panel--open' : ''}`}>
        <div className="newsletter-panel-inner">
          <p className="newsletter-panel-desc">{t.newsletter.description}</p>

          {status === 'success' ? (
            <div className="newsletter-success">
              <CheckCircle className="w-4 h-4" />
              <span>{message}</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="newsletter-form" noValidate>
              {/* Honeypot field – hidden from real users, bots fill it */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                <label htmlFor="nl_website">Website</label>
                <input
                  id="nl_website"
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              <div className="newsletter-input-row">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.newsletter.placeholder}
                  className="newsletter-input"
                  required
                  maxLength={255}
                  disabled={status === 'loading'}
                />
                <button
                  type="submit"
                  className="newsletter-submit"
                  disabled={!email || !gdprChecked || status === 'loading'}
                  aria-label="Subscribe to newsletter"
                >
                  {status === 'loading' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
              <label className="newsletter-gdpr">
                <input
                  type="checkbox"
                  checked={gdprChecked}
                  onChange={(e) => setGdprChecked(e.target.checked)}
                  className="newsletter-checkbox"
                  disabled={status === 'loading'}
                />
                <span>
                  {t.newsletter.gdpr}{' '}
                  <a href={withLangAndTheme('https://haikoinnovations.com/privacy', language, theme)} className="newsletter-gdpr-link" target="_blank" rel="noopener noreferrer">
                    {t.newsletter.gdprLink}
                  </a>
                  {t.newsletter.gdprSuffix}
                </span>
              </label>
              {status === 'error' && <p className="newsletter-error">{message}</p>}
            </form>
          )}
        </div>
      </div>

      {/* Toggle button */}
      <button
        className="newsletter-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <Mail className="w-4 h-4" />
        <span>{t.newsletter.subscribe}</span>
        <ChevronUp className={`w-3.5 h-3.5 newsletter-chevron ${isOpen ? 'newsletter-chevron--open' : ''}`} />
      </button>
    </div>
  );
}
